import { useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Copy, MoreVertical, UserRound } from 'lucide-react';
import { Link } from 'react-router';

import { Card } from '@/components/Card/Card';
import { Button } from '@/components/Button/Button';

import { CopyCourseDialog } from './CopyCourseDialog';
import {
  getBannerUrlForFilename,
  getCourseBannerUrl,
} from './courseBannerImages';
import { StatusBadge } from './StatusBadge';
import type { CourseListItem } from './useCourses';
import styles from './CourseCard.module.css';

interface CourseCardProps {
  bannerImageFilename?: string | null;
  course: CourseListItem;
  showCopyAction?: boolean;
}

type BannerTone = 'accent' | 'info' | 'neutral' | 'success' | 'warning';

function getStatusBadge(course: CourseListItem) {
  if (course.isArchived) {
    return {
      label: 'Archived',
      tone: 'archived' as const,
    };
  }

  if (course.isComplete) {
    return {
      label: 'Completed',
      tone: 'completed' as const,
    };
  }

  if (course.isLive) {
    return {
      label: 'Live',
      tone: 'live' as const,
    };
  }

  return null;
}

function getBannerTone(course: CourseListItem): BannerTone {
  if (course.iconVariant === 'subject') {
    return 'accent';
  }

  const total = [...course.id].reduce(
    (sum, character) => sum + character.charCodeAt(0),
    0,
  );
  const tones: BannerTone[] = ['info', 'success', 'warning', 'neutral'];

  return tones[total % tones.length] ?? 'neutral';
}

export function CourseCard({
  bannerImageFilename = null,
  course,
  showCopyAction = false,
}: CourseCardProps) {
  const statusBadge = getStatusBadge(course);
  const bannerTone = getBannerTone(course);
  const bannerUrl =
    course.bannerUrl ??
    getBannerUrlForFilename(bannerImageFilename) ??
    getCourseBannerUrl(course.parentSubject, course.id);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  // Copy opens its dialog once the menu has closed and given focus back to its
  // button: opened at once, the menu's late focus return pulled focus out of
  // the dialog (#173), and the dialog takes that button as its opener.
  const isCopyChosenRef = useRef(false);

  return (
    <Card as="article" className={styles.card} variant="standard">
      {showCopyAction ? (
        <>
          <DropdownMenu.Root modal={false} onOpenChange={setIsMenuOpen} open={isMenuOpen}>
            <DropdownMenu.Trigger asChild>
              <Button
                aria-label="Open course actions"
                className={styles.menuTrigger}
                iconOnly
                size="small"
                variant="ghost"
              >
                <MoreVertical aria-hidden="true" size={18} />
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                className={styles.menuContent}
                onCloseAutoFocus={() => {
                  if (isCopyChosenRef.current) {
                    isCopyChosenRef.current = false;
                    setIsCopyDialogOpen(true);
                  }
                }}
                sideOffset={8}
              >
                <DropdownMenu.Item
                  className={styles.menuItem}
                  onSelect={() => {
                    isCopyChosenRef.current = true;
                    setIsMenuOpen(false);
                  }}
                >
                  <Copy aria-hidden="true" size={16} />
                  <span>Copy</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          <CopyCourseDialog
            courseId={course.id}
            courseTitle={course.title}
            onOpenChange={setIsCopyDialogOpen}
            open={isCopyDialogOpen}
          />
        </>
      ) : null}

      <Link className={styles.linkSurface} to={course.href}>
        <div
          aria-hidden="true"
          className={styles.banner}
          data-tone={bannerTone}
        >
          {bannerUrl ? (
            <img
              alt=""
              className={styles.bannerImage}
              loading="lazy"
              src={bannerUrl}
            />
          ) : null}
          {!bannerUrl ? (
            <div className={styles.bannerFallback}>
              <div className={styles.bannerMark}>{course.iconLabel}</div>
            </div>
          ) : null}
        </div>

        <div className={styles.infoSection}>
          <div
            className={styles.titleRow}
            data-has-menu={showCopyAction ? 'true' : 'false'}
          >
            <h2 className={styles.title} title={course.title}>
              {course.title}
            </h2>
            {statusBadge ? (
              <StatusBadge label={statusBadge.label} tone={statusBadge.tone} />
            ) : null}
          </div>
          <p className={styles.subtitle} title={course.id}>
            {course.id}
          </p>
        </div>

        <div className={styles.footer}>
          <span className={styles.footerLabel}>
            <UserRound
              aria-hidden="true"
              className={styles.footerIcon}
              size={16}
            />
            <span>Instructor: {course.instructorName}</span>
          </span>
        </div>
      </Link>
    </Card>
  );
}
